import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import SupplierCard from './components/SupplierCard';
import FilterPanel from './components/FilterPanel';
import SupplierMap from './components/SupplierMap';
import SupplierStats from './components/SupplierStats';
import { FullWidthSection, ContentWrapper } from '../../components/ui/Layout';
import { supabase } from '../../lib/supabase';
import { useLanguage } from '../../contexts/LanguageContext';

const Suppliers = () => {
  const { language } = useLanguage();
  const [filters, setFilters] = useState({
    country: 'all',
    sortBy: 'name',
    certifications: [],
    verifiedOnly: false
  });

  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('suppliers')
        ?.select(`
          *,
          country:countries(name_en, name_zh)
        `)
        ?.eq('is_verified', true);

      if (error) throw error;

      // Map to the format expected by the component
      const formattedData = data?.map(s => ({
        ...s,
        country_en: s?.country?.name_en,
        country_zh: s?.country?.name_zh,
        verified: s?.is_verified,
        // Mocking some fields that might not be in the table yet but are used in UI
        yearsInBusiness: Math.floor(Math.random() * 20) + 5,
        varietiesOffered: Math.floor(Math.random() * 10) + 2,
        logo: s?.logo_url || "https://img.rocket.new/generatedImages/rocket_gen_img_15d3254c6-1764533605179.png",
        logoAlt: s?.name_en
      }));

      setSuppliers(formattedData || []);
    } catch (error) {
      console.error('Failed to load suppliers:', error);
    } finally {
      setLoading(false);
    }
  };



  const filterSuppliers = (suppliers) => {
    let filtered = [...suppliers];

    if (filters?.country !== 'all') {
      filtered = filtered?.filter((s) => s?.country_en?.toLowerCase() === filters?.country);
    }

    if (filters?.verifiedOnly) {
      filtered = filtered?.filter((s) => s?.verified);
    }

    if (filters?.certifications?.length > 0) {
      filtered = filtered?.filter((s) =>
        filters?.certifications?.some((cert) =>
          s?.certifications?.some((sCert) => sCert?.toLowerCase()?.includes(cert))
        )
      );
    }

    filtered?.sort((a, b) => {
      switch (filters?.sortBy) {
        case 'years':
          return b?.yearsInBusiness - a?.yearsInBusiness;
        case 'varieties':
          return b?.varietiesOffered - a?.varietiesOffered;
        case 'name':
        default:
          return language === 'en' ?
            a?.name_en?.localeCompare(b?.name_en) :
            a?.name_zh?.localeCompare(b?.name_zh);
      }
    });

    return filtered;
  };

  const filteredSuppliers = filterSuppliers(suppliers);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      country: 'all',
      sortBy: 'name',
      certifications: [],
      verifiedOnly: false
    });
  };

  return (
    <>
      <Helmet>
        <title>{language === 'en' ? 'Verified African Coffee Suppliers | Africoffee' : '经过验证的非洲咖啡供应商 | Africoffee'}</title>
        <meta
          name="description"
          content={language === 'en' ? 'Connect with verified African coffee suppliers from Ethiopia, Kenya, Uganda, and Nigeria. Browse supplier profiles, certifications, and farm locations.' : '与来自埃塞俄比亚、肯尼亚、乌干达和尼日利亚的经过验证的非洲咖啡供应商建立联系。浏览供应商资料、认证和农场位置。'} />

      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-[60px]">
          <div className="bg-primary/5 border-b border-border">
            <FullWidthSection padding="" py="py-8">
              <ContentWrapper padding="px-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Building2" size={28} className="text-primary" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-heading font-bold text-foreground">
                      {language === 'en' ? 'Verified Suppliers' : '经过验证的供应商'}
                    </h1>
                    <p className="text-muted-foreground">
                      {language === 'en' ? 'Connect with trusted African coffee suppliers' : '与值得信赖的非洲咖啡供应商建立联系'}
                    </p>
                  </div>
                </div>
              </ContentWrapper>
            </FullWidthSection>
          </div>

          <FullWidthSection padding="" py="py-8">
            <ContentWrapper padding="px-5">
              <SupplierStats suppliers={filteredSuppliers} language={language} />

              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
                <div className="lg:col-span-1">
                  <div className="lg:hidden mb-4">
                    <button
                      onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-card border border-border rounded-lg hover:bg-muted transition-smooth">

                      <span className="font-medium text-foreground">
                        {language === 'en' ? 'Filters' : '筛选'}
                      </span>
                      <Icon name={isMobileFilterOpen ? 'ChevronUp' : 'ChevronDown'} size={20} />
                    </button>
                  </div>

                  <div className={`${isMobileFilterOpen ? 'block' : 'hidden'} lg:block`}>
                    <FilterPanel
                      filters={filters}
                      onFilterChange={handleFilterChange}
                      onReset={handleResetFilters}
                      language={language} />

                  </div>
                </div>

                <div className="lg:col-span-3">
                  <div className="mb-6">
                    <SupplierMap suppliers={filteredSuppliers} language={language} />
                  </div>

                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-heading font-semibold text-foreground">
                      {language === 'en' ?
                        `${filteredSuppliers?.length} Suppliers Found` :
                        `找到 ${filteredSuppliers?.length} 个供应商`}
                    </h2>
                  </div>

                  {filteredSuppliers?.length > 0 ?
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {filteredSuppliers?.map((supplier) =>
                        <SupplierCard
                          key={supplier?.id}
                          supplier={supplier}
                          language={language} />

                      )}
                    </div> :

                    <div className="bg-card border border-border rounded-lg p-12 text-center">
                      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon name="Search" size={32} className="text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                        {language === 'en' ? 'No Suppliers Found' : '未找到供应商'}
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        {language === 'en' ? 'Try adjusting your filters to see more results' : '尝试调整筛选条件以查看更多结果'}
                      </p>
                      <button
                        onClick={handleResetFilters}
                        className="text-primary hover:underline font-medium">

                        {language === 'en' ? 'Reset Filters' : '重置筛选'}
                      </button>
                    </div>
                  }
                </div>
              </div>
            </ContentWrapper>
          </FullWidthSection>
        </main>

        <footer className="bg-card border-t border-border mt-12">
          <FullWidthSection padding="" py="py-8">
            <ContentWrapper padding="px-5">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Icon name="Coffee" size={24} className="text-primary" />
                  <span className="text-lg font-heading font-semibold text-foreground">Africoffee</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  © {new Date()?.getFullYear()} Africoffee. {language === 'en' ? 'All rights reserved.' : '版权所有。'}
                </p>
              </div>
            </ContentWrapper>
          </FullWidthSection>
        </footer>
      </div>
    </>);

};

export default Suppliers;